import React, {FC, createContext, useCallback, useContext, useEffect, useMemo, useState} from 'react';
import {useApp} from '@app';
import {getAuth} from '@infra/api/authManager/authManager';
import {eventManager} from '@application/Event';
import {CheckoutEvent} from '@domain/model/Event';
import debounce from 'lodash.debounce';
import {useFeatureFlags} from '../../../../ui/components/_context/FeatureFlagContext';
import {getStore, getUserData} from '@tracking/user-data';
import {emitUserEvent} from '@lookiero/user-tracking-front';
import {ConfigurationApplication} from '@application/Config';
import {isLookInEnabled} from '@modules/look-in/bootstrap';

const {tradename} = ConfigurationApplication.retrieve();

type LookInEvent = 'AFTER_CHECKOUT_ACCESS_GRANTED' | 'CHECKOUT_PAYMENT_INFO_ADDED' | 'PAYMENT' | 'SHOP_BUTTON_CLICKED';

const LOOK_IN_EVENT: Record<LookInEvent, string> = {
  PAYMENT: 'payment',
  AFTER_CHECKOUT_ACCESS_GRANTED: 'after_checkout_access_granted',
  CHECKOUT_PAYMENT_INFO_ADDED: 'checkout:payment-info-added',
  SHOP_BUTTON_CLICKED: 'shop_click_button_1',
};

interface EmitUserEventFunctionArgs {
  readonly event: LookInEvent;
  readonly payload?: Record<string, unknown>;
}

interface EmitUserEventFunction {
  (args: EmitUserEventFunctionArgs): Promise<void>;
}

interface LookInContext {
  readonly enabled: boolean;
  readonly salesOfferingEnabled: boolean;
  readonly loading: boolean;
  readonly emitUserEvent: EmitUserEventFunction;
}

interface LookInProviderProps {
  readonly children: React.ReactNode;
}

const LookInContext = createContext<LookInContext>({} as LookInContext);

const LookInProvider: FC<LookInProviderProps> = ({children}) => {
  const {authorization} = useApp();
  const [authToken, setAuthToken] = useState<string>();
  const [enabled, setEnabled] = useState<boolean>(false);
  const [salesOfferingEnabled, setSalesOfferingEnabled] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);

  const featureFlags = useFeatureFlags();

  const emitEvent: EmitUserEventFunction = useCallback(async ({event, payload}) => {
    const {id, segment} = await getUserData();
    const store = getStore();

    emitUserEvent({
      event: LOOK_IN_EVENT[event],
      eventCategory: 'shop',
      userId: id,
      userUuid: id,
      segment: segment,
      store,
      tradename,
      ...payload,
    });
  }, []);

  useEffect(() => {
    (async () => {
      const token = await getAuth();

      if (token) {
        setAuthToken(`Bearer ${token}`);
      }
    })();
  }, [authorization]);

  const debouncedRefetchAccesesAfterCheckout = useMemo(
    () =>
      debounce(async () => {
        const {offerings} = await isLookInEnabled();

        emitEvent({event: 'AFTER_CHECKOUT_ACCESS_GRANTED', payload: {enabledOfferings: offerings}});
      }, 100),
    [emitEvent],
  );

  useEffect(() => {
    eventManager.subscribe(CheckoutEvent.CHECKOUT_SUBMITTED, debouncedRefetchAccesesAfterCheckout);

    return () => eventManager.unsubscribe(CheckoutEvent.CHECKOUT_SUBMITTED, debouncedRefetchAccesesAfterCheckout);
  });

  useEffect(() => {
    const loadOfferings = async (): Promise<void> => {
      const lookInFeatureFlag = featureFlags.DIRECT_BUY || false;
      const {enabled: lookInEnabled, offerings} = await isLookInEnabled();
      setSalesOfferingEnabled(lookInFeatureFlag && offerings.sales);
      setEnabled(lookInFeatureFlag && lookInEnabled);
    };

    setLoading(!authToken || featureFlags.DIRECT_BUY === undefined);
    loadOfferings();
  }, [authToken, featureFlags.DIRECT_BUY]);

  const value = useMemo(
    () => ({
      enabled,
      salesOfferingEnabled,
      loading,
      emitUserEvent: emitEvent,
    }),
    [enabled, salesOfferingEnabled, loading, emitEvent],
  );

  return <LookInContext.Provider value={value}>{children}</LookInContext.Provider>;
};

const useLookIn = (): LookInContext => useContext(LookInContext);

export {LookInProvider, useLookIn};
